import React, { useEffect, useState, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// Sidebar icons LIGHT
import { ReactComponent as LogoutIcon } from '../../assets/icons/logout.svg';
import { ReactComponent as FinanceActiveIconLight } from '../../assets/icons/sidebar/finance-active.svg';
import { ReactComponent as FinanceIconLight } from '../../assets/icons/sidebar/finance.svg';
import { ReactComponent as OperationsActiveIconLight } from '../../assets/icons/sidebar/operations-active.svg';
import { ReactComponent as OperationsIconLight } from '../../assets/icons/sidebar/operations.svg';
import { ReactComponent as PowerByEnergic } from '../../assets/icons/sidebar/powered-by-energic.svg';
import { ReactComponent as ReportsActiveIconLight } from '../../assets/icons/sidebar/reports-active.svg';
import { ReactComponent as ReportsIconLight } from '../../assets/icons/sidebar/reports.svg';
import { ReactComponent as SettingsActiveIconLight } from '../../assets/icons/sidebar/settings-active.svg';
import { ReactComponent as SettingsIconLight } from '../../assets/icons/sidebar/settings.svg';
import { ReactComponent as DashboardActiveIconLight } from '../../assets/icons/sidebar/sidebar-dashboard-active.svg';
import { ReactComponent as DashboardIconLight } from '../../assets/icons/sidebar/sidebar-dashboard.svg';
import { ReactComponent as ToolsActiveIconLight } from '../../assets/icons/sidebar/tools-active.svg';
import { ReactComponent as ToolsIconLight } from '../../assets/icons/sidebar/tools.svg';
import { ReactComponent as FinanceActiveIconDark } from '../../assets/icons/sidebarDark/finance-active-dark.svg';
import { ReactComponent as FinanceIconDark } from '../../assets/icons/sidebarDark/finance-dark.svg';
import { ReactComponent as OperationsActiveIconDark } from '../../assets/icons/sidebarDark/operations-active-dark.svg';
import { ReactComponent as OperationsIconDark } from '../../assets/icons/sidebarDark/operations-dark.svg';
import { ReactComponent as ReportsActiveIconDark } from '../../assets/icons/sidebarDark/reports-dark-active.svg';
import { ReactComponent as ReportsIconDark } from '../../assets/icons/sidebarDark/reports-dark.svg';
import { ReactComponent as SettingsActiveIconDark } from '../../assets/icons/sidebarDark/settings-active-dark.svg';
import { ReactComponent as SettingsIconDark } from '../../assets/icons/sidebarDark/settings-dark.svg';
import { ReactComponent as DashboardActiveIconDark } from '../../assets/icons/sidebarDark/sidebar-dashboard-active-dark.svg';
import { ReactComponent as DashboardIconDark } from '../../assets/icons/sidebarDark/sidebar-dashboard-dark.svg';
import { ReactComponent as ToolsActiveIconDark } from '../../assets/icons/sidebarDark/tools-active-dark.svg';
import { ReactComponent as ToolsIconDark } from '../../assets/icons/sidebarDark/tools-dark.svg';
import { brandImage, useAppBranding } from '../../config/branding';
import ThemeContext from '../../contexts/theme-context/ThemeContext';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { getCookie, removeCookie } from '../../shared-functions/Cookies';
import { checkPermission } from '../../shared-functions/JWTDocodeFunction';
import { removeLocalStorageItem } from '../../shared-functions/LocalStorage';
import authApi from '../../utils/api/auth';
import LanguageSelector from '../language-selector/LanguageSelector';

// Define types for menu items

/**
 * ChevronDown component
 * @returns ChevronDown component
 */
function ChevronDown() {
  return (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1 1L5 5L9 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * ChevronRight component
 * @returns ChevronRight component
 */
function ChevronRight() {
  return (
    <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1 9L5 5L1 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface MenuItem {
  id: string;
  label: string;
  url?: string;
  svgIcon?: React.ReactNode | (() => React.ReactNode);
  activeSvgIcon?: React.ReactNode | (() => React.ReactNode);
  allow: boolean;
  children?: MenuItem[];
  isOpen?: boolean;
  activeMatch?: string;
}

/**
 * renderIcon component
 */
const renderIcon = (icon: React.ReactNode | (() => React.ReactNode)) => {
  if (typeof icon === 'function') {
    return (icon as () => React.ReactNode)();
  }
  return icon;
};
/**
 * Shared sidebar component
 */
function Sidebar() {
  const { t, i18n } = useTranslation('sharedComp');
  const branding = useAppBranding();
  const { theme } = useContext(ThemeContext);
  const sidebarReference = useRef<HTMLDivElement>(null);
  const [expandSidebar, setExpandSidebar] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isRtl = i18n.language === 'ar';
  const cachedLocal = JSON.parse(localStorage.getItem('app_branding_config') || ('{}' as any));

  /**
   * getThemeIcons
   */
  const getThemeIcons = (lightIcon: React.ReactNode, darkIcon: React.ReactNode) => {
    return theme === 'light' ? lightIcon : darkIcon;
  };
  /**
   * sidebar columns
   */
  const [menuItems, setMenuItems] = useState<MenuItem[]>([
    {
      id: 'dashboard',
      label: 'Dashboard',
      url: '/ev-admin/dashboard',
      allow: checkPermission(['dashboard']),
      /**
       * svg icon
       */
      svgIcon: () => getThemeIcons(<DashboardIconLight />, <DashboardIconDark />),
      /**
       * active icon
       */
      activeSvgIcon: () => getThemeIcons(<DashboardActiveIconLight />, <DashboardActiveIconDark />),
    },
    {
      id: 'operations',
      label: 'Operations',
      allow: true,
      /**
       * svg icon
       */
      svgIcon: () => getThemeIcons(<OperationsIconLight />, <OperationsIconDark />),
      /**
       * active icon
       */
      activeSvgIcon: () =>
        getThemeIcons(<OperationsActiveIconLight />, <OperationsActiveIconDark />),
      children: [
        {
          id: 'facility',
          label: 'Facility',
          url: '/ev-admin/facility-management',
          allow: checkPermission(['charging_station']),
        },
        {
          id: 'chargingStations',
          label: 'Charging Stations',
          url: '/ev-admin/station-management/approved',
          activeMatch: '/ev-admin/station-management',
          allow: checkPermission(['charging_station']),
        },
        {
          id: 'chargers',
          label: 'Chargers',
          url: '/ev-admin/device-management',
          allow: checkPermission(['charging_point']),
        },
        {
          id: 'sessions',
          label: 'Sessions',
          url: '/ev-admin/charging-session',
          allow: true,
        },
        {
          id: 'guestCharging',
          label: 'Guest Charging',
          url: '/ev-admin/guest-charging',
          allow: checkPermission(['reports']),
        },
      ],
    },
    {
      id: 'finance',
      label: 'Finance',
      /**
       * svg icon
       */
      svgIcon: () => getThemeIcons(<FinanceIconLight />, <FinanceIconDark />),
      /**
       * active icon
       */
      activeSvgIcon: () => getThemeIcons(<FinanceActiveIconLight />, <FinanceActiveIconDark />),
      allow: true,
      children: [
        {
          id: 'revenue',
          label: 'Revenue',
          allow: true,
          children: [
            {
              id: 'transactionManagement',
              label: t('transactionManagement'),
              url: '/ev-admin/transaction-management',
              allow: checkPermission(['transaction_management']),
            },
          ],
        },
        {
          id: 'configuration',
          label: 'Configuration',
          allow: true,
          children: [
            {
              id: 'dynamicPricing',
              label: t('dynamicPricing'),
              url: '/ev-admin/dynamic-pricing',
              allow: checkPermission(['dynamic_pricing']),
            },
            {
              id: 'idleFees',
              label: 'Idle Fee',
              url: '/ev-admin/penalty-management/request',
              allow: true,
            },
            {
              id: 'scheduleManagement',
              label: 'Charging Schedule',
              url: '/ev-admin/schedule-management',
              allow: checkPermission(['charging_schedule']),
            },
          ],
        },
      ],
    },
    {
      id: 'reports',
      label: 'Reports',
      allow: true,
      /**
       * svg icon
       */
      svgIcon: () => getThemeIcons(<ReportsIconLight />, <ReportsIconDark />),
      /**
       * active icon
       */
      activeSvgIcon: () => getThemeIcons(<ReportsActiveIconLight />, <ReportsActiveIconDark />),
      children: [
        {
          id: 'energyReport',
          label: 'Energy Report',
          url: '/ev-admin/reports/energyReport',
          allow: true,
        },
        {
          id: 'revenueReport',
          label: 'Revenue Report',
          url: '/ev-admin/reports/revenueReport',
          allow: true,
        },
        {
          id: 'bookingsReport',
          label: 'Bookings Report',
          url: '/ev-admin/reports/bookingsReport',
          allow: true,
        },
        {
          id: 'chargerUtilizationReport',
          label: 'Charger Utilization Report',
          url: '/ev-admin/reports/chargerUtilizationReport',
          allow: true,
        },
        {
          id: 'performanceReport',
          label: 'Performance Report',
          url: '/ev-admin/reports/performanceReport',
          allow: true,
        },
        {
          id: 'maintenanceReport',
          label: 'Maintenance Report',
          url: '/ev-admin/reports/maintenanceReport',
          allow: true,
        },
      ],
    },
    {
      id: 'settings',
      label: 'Settings',
      allow: true,
      /**
       * svg icon
       */
      svgIcon: () => getThemeIcons(<SettingsIconLight />, <SettingsIconDark />),
      /**
       * active icon
       */
      activeSvgIcon: () => getThemeIcons(<SettingsActiveIconLight />, <SettingsActiveIconDark />),
      children: [
        {
          id: 'users',
          label: 'Users',
          url: '/ev-admin/user-management/user',
          allow: checkPermission(['user_management']),
        },
        {
          id: 'roles',
          label: 'Roles',
          url: '/ev-admin/user-management/role',
          allow: checkPermission(['user_management']),
        },
      ],
    },
    {
      id: 'tools',
      label: 'Tools',
      allow: true,
      /**
       * svg icon
       */
      svgIcon: () => getThemeIcons(<ToolsIconLight />, <ToolsIconDark />),
      /**
       * active icon
       */
      activeSvgIcon: () => getThemeIcons(<ToolsActiveIconLight />, <ToolsActiveIconDark />),
      children: [
        {
          id: 'virtualLoadBalancer',
          label: t('virtualLoadBalancer'),
          url: '/ev-admin/vlbc',
          allow: true,
        },
        {
          id: 'maintenanceScheduling',
          label: t('maintenanceScheduling'),
          url: '/ev-admin/maintenance-scheduling',
          activeMatch: '/ev-admin/maintenance-scheduling',
          allow: checkPermission(['schedule_maintenance']),
        },
      ],
    },
  ]);

  /* Accordion Logic: Auto-collapse other root items when one opens */
  /**
   * Update the menu open state
   * @param items - Menu items
   * @param id - Target ID
   * @param depth - Recursion depth
   * @returns Updated menu items
   */
  function updateMenuOpenState(items: MenuItem[], id: string, depth: number = 0): MenuItem[] {
    // Check if the target ID is a direct child of this list (to handle root exclusive toggle)
    const isTargetRoot = depth === 0 && items.some((item) => item.id === id);

    if (isTargetRoot) {
      return items.map((item) => ({
        ...item,
        isOpen: item.id === id ? !item.isOpen : false, // Close others if root
      }));
    }

    return items.map((item) => {
      if (item.id === id) {
        return { ...item, isOpen: !item.isOpen };
      }
      if (item.children) {
        return { ...item, children: updateMenuOpenState(item.children, id, depth + 1) };
      }
      return item;
    });
  }

  /**
   * Toggle submenu
   * @param id - Menu item ID
   */
  function toggleSubMenu(id: string) {
    // Simply toggle the menu item state.
    // Floating popups will handle the display if sidebar is collapsed.
    setMenuItems((previous) => updateMenuOpenState(previous, id));
  }

  // Helper to strictly close all menus (used when navigating from floating popup)
  /**
   * Close all menus
   * @param items - Menu items
   * @returns Closed menu items
   */
  function closeAllMenus(items: MenuItem[]): MenuItem[] {
    return items.map((item) => ({
      ...item,
      isOpen: false,
      children: item.children ? closeAllMenus(item.children) : undefined,
    }));
  }

  /**
   * Handle navigation
   * @param url - Target URL
   */
  function handleNavigate(url?: string) {
    if (url) {
      if (!expandSidebar) {
        setMenuItems((previous) => closeAllMenus(previous));
      }
      navigate(url);
    }
  }

  /**
   * Logout function
   */
  const handleLogout = () => {
    const accessToken = getCookie('ev-access-token');
    authApi
      .logout({ accessToken })
      .then(() => {
        removeCookie('ev-refresh-token');
        removeCookie('ev-access-token');
        removeLocalStorageItem('accountVerificationStatus');
        navigate('../');
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Check if route is active
   * @param itemUrl - URL to check
   * @param activeMatch - Custom match string
   * @returns True if active
   */
  const isRouteActive = React.useCallback(
    (itemUrl?: string, activeMatch?: string) => {
      if (activeMatch) {
        return location.pathname.includes(activeMatch);
      }
      if (!itemUrl) return false;
      return location.pathname.includes(itemUrl) && itemUrl !== '/ev-admin';
    },
    [location.pathname],
  );

  // Helper to check if any child is active
  /**
   * Check if parent is active
   * @param item - Menu item
   * @returns True if active
   */
  const isParentActive = React.useCallback(
    (item: MenuItem): boolean => {
      if (item.url && isRouteActive(item.url, item.activeMatch)) return true;
      if (item.children) {
        return item.children.some((child) => isParentActive(child));
      }
      return false;
    },
    [isRouteActive],
  );

  // Render a single menu item (recursive)
  /**
   * Render menu item
   * @param item - Menu item
   * @param depth - Depth
   * @returns JSX element
   */
  function renderMenuItem(item: MenuItem, depth: number = 0) {
    const { allow, children, url, isOpen, id, svgIcon, activeSvgIcon, activeMatch } = item;
    if (!allow) return null;

    const hasChildren = children && children.length > 0;
    const isActive = isRouteActive(url, activeMatch);
    const isParent = hasChildren && isParentActive(item);

    return (
      <div key={id} className={`menu-item-container depth-${depth}`}>
        <div
          className={`sidebar-menu-item ${isActive ? 'active' : ''} ${isParent || (hasChildren && isOpen) ? 'parent-active' : ''}`}
          onClick={() => {
            if (hasChildren) {
              toggleSubMenu(id);
            } else {
              handleNavigate(url);
            }
          }}
          title={!expandSidebar && depth === 0 ? t(id) : ''}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              if (hasChildren) {
                toggleSubMenu(id);
              } else {
                handleNavigate(url);
              }
            }
          }}>
          {depth === 0 && (
            <div className={`icon-container ${isActive || isParent ? 'active' : ''}`}>
              <span className="menu-icon">
                {isActive || isParent ? renderIcon(activeSvgIcon) : renderIcon(svgIcon)}
              </span>
            </div>
          )}

          {/* Show label if Expanded OR if we are in a floating popup (depth > 0) */}
          {(expandSidebar || depth > 0) && (
            <div className="label-container">
              <span className="menu-label">{t(id)}</span>
              {hasChildren && (
                <span className="chevron">{isOpen ? <ChevronDown /> : <ChevronRight />}</span>
              )}
            </div>
          )}
        </div>

        {/* Render children if open. 
            Use 'submenu-container' for inline tree (expanded sidebar).
            Use 'floating-menu-container' for popup menu (collapsed sidebar or nested popup). 
        */}
        {hasChildren && isOpen && (
          <div className={expandSidebar ? 'submenu-container' : 'floating-menu-container'}>
            {!expandSidebar && (
              <div className="floating-header">
                {svgIcon && <span className="header-icon">{renderIcon(svgIcon)}</span>}
                {t(id)}
              </div>
            )}
            {children!.map((child) => renderMenuItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  useEffect(() => {
    /**
     * Open active menus
     * @param items - Menu items
     * @returns Updated menu items
     */
    const openActiveMenus = (items: MenuItem[]): MenuItem[] => {
      return items.map((item) => {
        const active = isParentActive(item);
        const newItem = { ...item };
        // Only auto-expand if sidebar is expanded.
        // If collapsed (floating mode), do NOT auto-open on route change.
        if (expandSidebar && active && item.children) {
          newItem.isOpen = true;
          newItem.children = openActiveMenus(item.children);
        } else if (item.children) {
          newItem.children = openActiveMenus(item.children);
        }
        return newItem;
      });
    };
    setMenuItems((previous) => openActiveMenus(previous));
  }, [location.pathname, expandSidebar, isParentActive]);

  const [appVersion, setAppVersion] = useState<string>('');

  /**
   * handleVersion fetches the current app version from version.txt
   */
  const handleVersion = async () => {
    try {
      // ✅ Automatically pick current domain
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/version.txt`;

      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const text = await response.text();

      // ❗ Avoid HTML error response
      if (!text || text.includes('<!DOCTYPE html>')) {
        setAppVersion('');
        return;
      }

      setAppVersion(text.trim());
    } catch (error) {
      console.error('Version fetch failed:', error);
      setAppVersion('');
    }
  };

  useEffect(() => {
    handleVersion();
  }, []);

  // Compute toggle arrow rotation:
  // LTR: expanded → left (180°), collapsed → right (0°)
  // RTL: expanded → right (0°),  collapsed → left (180°)
  const toggleArrowRotation = (isRtl ? !expandSidebar : expandSidebar)
    ? 'rotate(180deg)'
    : 'rotate(0deg)';

  return (
    <div className="sidebar-wrap" dir={i18n.dir()}>
      <div
        className={`sidebar-container ${expandSidebar ? 'expand' : 'close'} ${isRtl ? 'rtl' : ''}`}
        ref={sidebarReference}>
        <div className="sidebar-logo">
          <img
            src={brandImage.logo}
            alt="brand-logo"
            className={cachedLocal?.clientName === 'saqr-tech' ? 'saqr-tech' : ''}
          />
        </div>
        <div
          className="toggle-button"
          aria-label={
            expandSidebar
              ? t('collapseSidebar', 'Collapse sidebar')
              : t('expandSidebar', 'Expand sidebar')
          }
          onClick={() => {
            if (expandSidebar) {
              setMenuItems((previous) => closeAllMenus(previous));
            }
            setExpandSidebar(!expandSidebar);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              if (expandSidebar) {
                setMenuItems((previous) => closeAllMenus(previous));
              }
              setExpandSidebar(!expandSidebar);
            }
          }}>
          <svg
            width="6"
            height="8"
            viewBox="0 0 6 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ transform: toggleArrowRotation, transition: 'transform 0.3s ease' }}>
            <path
              d="M1 9L5 5L1 1"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="sidebar-scroll-area">{menuItems.map((item) => renderMenuItem(item))}</div>
        <div className={`sidebar-language-selector ${!expandSidebar ? 'close' : ''}`}>
          <LanguageSelector isCollapsed={!expandSidebar} />
        </div>
        <div
          className="sidebar-logout"
          role="button"
          tabIndex={0}
          onClick={handleLogout}
          onKeyDown={(event) => event.key === 'Enter' && handleLogout()}>
          <span className="logout-icon">
            <LogoutIcon />
          </span>
          {expandSidebar && <span className="logout-text">{t('logout')}</span>}
        </div>
        <div className="app-version">
          {branding.meta.footerText && <PowerByEnergic />}
          <span>{appVersion}</span>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
